import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '@/lib/cn';
import { DIALOG_SIZES } from './dialog';
import { surfaceClassName, useSurface } from './surface.context';

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogCancel = AlertDialogPrimitive.Cancel;
export const AlertDialogAction = AlertDialogPrimitive.Action;

/** A dialog that must be answered: no X, and a click outside does not dismiss it silently. */
export function AlertDialogContent({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & { size?: keyof typeof DIALOG_SIZES }) {
  const surface = useSurface();
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay data-slot="dialog-overlay" className="fixed inset-0 z-dialog bg-overlay backdrop-blur-sm" />
      <AlertDialogPrimitive.Content
        data-slot="dialog-content"
        data-alert=""
        className={cn(
          surfaceClassName(surface),
          'fixed left-1/2 top-1/2 z-dialog-content max-h-[90vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto',
          'rounded-card border border-line bg-surface p-6 text-ink shadow-e4 outline-none',
          DIALOG_SIZES[size],
          className,
        )}
        {...props}
      />
    </AlertDialogPrimitive.Portal>
  );
}

export function AlertDialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

export function AlertDialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-footer" className={cn('mt-5 flex justify-end gap-2', className)} {...props} />;
}

export function AlertDialogTitle({ className, ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return <AlertDialogPrimitive.Title data-slot="dialog-title" className={cn('text-base font-semibold text-ink', className)} {...props} />;
}

export function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return <AlertDialogPrimitive.Description className={cn('text-sm text-ink-2', className)} {...props} />;
}
